import { BonusIcon, Label, TTranslator } from '@smartshell/ui-kit';
import day from 'dayjs';
import { Column } from 'react-table';

import { CompositePaymentTooltip, PaymentItemsTooltip } from 'components';
import { TLocalesKeys } from 'localization';
import { EPaymentMethod, IPaymentHistoryItem, TPayment } from 'types';
import { formatSum, getPaymentMethodLabel } from 'utils';

import { DATE_KEY } from './constants';
import s from './WorkShiftPayments.module.scss';

export const getColumns = (
  currency: string,
  t: TTranslator<TLocalesKeys>
): Array<Column<TPayment>> => [
  { Header: t('payment.number'), accessor: 'id', minWidth: 138 },
  {
    id: DATE_KEY,
    Header: t('payment.date'),
    accessor: ({ created_at }) =>
      created_at ? day(created_at).valueOf() : null,
    Cell: ({ value }: { value: number }) => (
      <span title={day(value).format('D MMM YYYY HH:mm:ss')}>
        {day(value).format('DD MMM YYYY HH:mm')}
      </span>
    ),
    minWidth: 170,
  },
  {
    Header: t('category'),
    accessor: 'items',
    Cell: ({ value }: { value: IPaymentHistoryItem['items'] }) =>
      value.length === 1 &&
      (value[0]?.type === 'DEPOSIT' || value[0]?.type === 'BONUS') ? (
        value[0]?.type === 'DEPOSIT' ? (
          t('payments.category.deposit')
        ) : (
          t('payments.category.bonuses')
        )
      ) : (
        <PaymentItemsTooltip items={value} />
      ),
    minWidth: 186,
  },
  {
    id: 'client',
    Header: t('client'),
    accessor: ({ client }) =>
      client?.nickname ?? client?.phone ?? t('client.guest'),
    Cell: ({ value }: { value: string }) => {
      return (
        <span title={value} className={s['user']}>
          {value}
        </span>
      );
    },
    minWidth: 155,
  },
  {
    Header: t('payment'),
    accessor: 'method',
    Cell: ({ value }) => getPaymentMethodLabel(value, t),
    minWidth: 129,
  },
  {
    Header: t('total'),
    accessor: 'sum',
    Cell: ({
      value,
      row: {
        original: { method, card_sum = 0, cash_sum = 0, items },
      },
    }) =>
      method === EPaymentMethod.COMPOSITE ? (
        <CompositePaymentTooltip
          cash={cash_sum}
          card={card_sum}
          total={value}
        />
      ) : items[0].type !== 'BONUS' ? (
        formatSum(value, currency, { maximumFractionDigits: 2 })
      ) : (
        <div className={s.bonuses}>
          <BonusIcon size="s" />
          {formatSum(value, 'empty', { maximumFractionDigits: 2 })}
        </div>
      ),
    minWidth: 128,
  },
  {
    accessor: 'is_refunded',
    Cell: ({ value }) =>
      value ? (
        <Label
          color="gray-primary"
          text={t('payment.refunded')}
          iconName="CircleCloseIcon"
        />
      ) : null,
    width: 210,
    disableSortBy: true,
    disableResizing: true,
  },
];
